import { useCallback, useContext } from 'react';

import { Context } from 'components/hoc/Store';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import cisMenyApi from 'services/cisMeny';
import cisStatyApi from 'services/cisStaty';
import leiPrehledUctuApi from 'services/lei/PrehledUctu';
import leiAktualizace from 'services/lei/leiAktualizace';
import { EStavZadostiLoginlessLei } from 'services/lei/leiLoginLess';
import { IPlatbaLogin } from 'services/lei/leiPlatba';
import { leiPrevodGet } from 'services/lei/leiTransfer';
import leiZadostiApi from 'services/lei/leiZadosti';
import leiDataApi from 'services/lei/novyLeiData';
import nastavitUcetApi from 'services/nastavitUcet';
import profilyApi from 'services/profily';

import { ETypZadosti } from 'enums/lei/leiTypZadosti';
import routePathEnum from 'enums/routePathEnum';

export const useLeiRedirect = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { cisStaty, lei, platbaLei } = useContext(Context);
    const { setLevel } = useProfily();

    const removeEmptyProperties = (obj: any) => {
        const result: any = {};

        for (const key in obj) {
            if (Object.hasOwnProperty.call(obj, key)) {
                const value = obj[key];

                // Check for non-empty values
                if (value !== null && value !== undefined && value !== '') {
                    result[key] = value;
                }
            }
        }

        return result;
    };

    const loginlessOsoba = {
        contactname: lei?.loginless?.osoba?.jmeno,
        contactsurname: lei?.loginless?.osoba?.prijmeni,
        contactemail: lei?.loginless?.osoba?.email1,
        contactemail2: lei?.loginless?.osoba?.email2,
        fakturacniemail: lei?.loginless?.osoba?.emailfakt,
        contactphone: lei?.loginless?.osoba?.telefon,
        contactjob: lei?.loginless?.osoba?.pozice,
        typosoby: lei?.loginless?.osoba?.typosoby,
        madic: lei?.loginless?.osoba?.madic,
        ...(lei?.loginless?.osoba?.dic && {
            dic: lei?.loginless?.osoba.dic
        })
    };

    const loginlessSpolecnost = {
        legalentityidentifier: lei?.loginless?.spolecnost?.legalentityidentifier,
        registrationauthorityentityid: lei?.loginless?.spolecnost?.registrationauthorityentityid,
        jurisdikce: lei?.loginless?.spolecnost?.jurisdikce,
        registrationauthorityid: lei?.loginless?.spolecnost?.regauthority
    };

    const leiRedirect = useCallback(
        (idZadosti: number, varsym: string) => {
            console.log('func', lei);
            profilyApi().then(({ data }) => {
                setLevel('profily', data);
                if (cisStaty.length < 1) {
                    cisStatyApi().then(resp => {
                        setLevel('cisStaty', resp.data);
                    });
                    cisMenyApi().then(resp => {
                        setLevel('cisMeny', resp.data);
                    });
                }
                setLevel('ucty', data.sezpristupu!);
                const firmaLei = data.sezpristupu?.filter(e => e.typ === 'SPRAVCELEI')[0];
                nastavitUcetApi(firmaLei?.iducet || 0).then(() =>
                    leiPrehledUctuApi().then(rezp => {
                        firmaLei &&
                            setLevel('aktivniFirma', {
                                ...firmaLei
                            });
                        leiZadostiApi(varsym).then(zadosti => {
                            const zadost = zadosti.lei.filter(e => e.idzadosti === Number(idZadosti))[0];
                            zadost.typzadosti === ETypZadosti.REGISTRACE
                                ? leiDataApi('get', zadost.idzadosti, varsym!).then(dataLei => {
                                      setLevel('lei', {
                                          ...lei,
                                          selectedLei: rezp.lei.filter(e => e.varsymbol === varsym)[0],
                                          data: {
                                              ...dataLei,
                                              osoba: {
                                                  ...dataLei.osoba,
                                                  ...removeEmptyProperties(loginlessOsoba)
                                              },
                                              spolecnost: {
                                                  ...dataLei.spolecnost,
                                                  ...removeEmptyProperties(loginlessSpolecnost)
                                              }
                                          },
                                          idVarsymZadosti: {
                                              varsym: varsym!,
                                              id: zadost.idzadosti
                                          },
                                          datumPodani: zadost.datumpodani,
                                          stavZadosti: zadost.stav,
                                          // roznanie ci ide o loginless ziadost po presmerovani z platobnej brany
                                          ...(platbaLei && (platbaLei as IPlatbaLogin).jinastranka
                                              ? ({
                                                    loginless: {
                                                        osoba: {
                                                            stavzadosti: EStavZadostiLoginlessLei.HOTOVO
                                                        }
                                                    }
                                                } as any)
                                              : {})
                                      });
                                      navigateWorkaround(`/firma/${firmaLei?.iducet}${routePathEnum.LEI_NOVY}`);
                                  })
                                : zadost.typzadosti === ETypZadosti.AKTUALIZACE
                                  ? leiAktualizace('get', varsym, zadost.idzadosti).then(dataLei => {
                                        setLevel('lei', {
                                            ...lei,
                                            selectedLei: rezp.lei.filter(e => e.varsymbol === varsym)[0],
                                            detailData: {
                                                ...dataLei,
                                                osoba: {
                                                    ...dataLei.osoba,
                                                    ...removeEmptyProperties(loginlessOsoba)
                                                },
                                                spolecnost: {
                                                    ...dataLei.spolecnost,
                                                    ...removeEmptyProperties(loginlessSpolecnost)
                                                }
                                            },
                                            paymentDueDate: dataLei.paymentduedate,
                                            idVarsymZadosti: {
                                                varsym: varsym,
                                                id: zadost.idzadosti
                                            },
                                            stavZadosti: zadost.stav,
                                            datumPodani: zadost.datumpodani,
                                            ...(platbaLei && (platbaLei as IPlatbaLogin).jinastranka
                                                ? ({
                                                      loginless: {
                                                          osoba: {
                                                              stavzadosti: EStavZadostiLoginlessLei.HOTOVO
                                                          }
                                                      }
                                                  } as any)
                                                : {})
                                        });
                                        navigateWorkaround(`/firma/${firmaLei?.iducet}${routePathEnum.LEI_UPRAVA}`);
                                    })
                                  : leiPrevodGet(varsym, zadost.idzadosti).then(dataLei => {
                                        setLevel('lei', {
                                            ...lei,
                                            selectedLei: rezp.lei.filter(e => e.varsymbol === varsym)[0],
                                            prevodData: {
                                                ...dataLei,
                                                osoba: {
                                                    ...dataLei.osoba,
                                                    ...removeEmptyProperties(loginlessOsoba)
                                                },
                                                spolecnost: {
                                                    ...dataLei.spolecnost,
                                                    ...removeEmptyProperties(loginlessSpolecnost)
                                                }
                                            },
                                            idVarsymZadosti: {
                                                varsym: varsym,
                                                id: zadost.idzadosti
                                            },
                                            stavZadosti: zadost.stav,
                                            datumPodani: zadost.datumpodani,
                                            ...(platbaLei && (platbaLei as IPlatbaLogin).jinastranka
                                                ? ({
                                                      loginless: {
                                                          osoba: {
                                                              stavzadosti: EStavZadostiLoginlessLei.HOTOVO
                                                          }
                                                      }
                                                  } as any)
                                                : {})
                                        });
                                        navigateWorkaround(`/firma/${firmaLei?.iducet}${routePathEnum.LEI_DETAIL}`);
                                    });
                        });
                    })
                );
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lei]
    );

    return {
        leiRedirect
    };
};
